import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus, Trash2, Shield, User, Loader2, KeyRound, Server, FlaskConical } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changePassword, setChangePassword] = useState('');
  const [adding, setAdding] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [error, setError] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.data.users || []);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const handleAddUser = async () => {
    setError('');
    setAdding(true);

    try {
      const res = await fetch(`${API}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: newEmail, name: newName, password: newPassword })
      });

      const data = await res.json();

      if (res.ok) {
        setShowAddDialog(false);
        setNewEmail('');
        setNewName('');
        setNewPassword('');
        fetchUsers();
      } else {
        const detail = data.detail;
        setError(typeof detail === 'string' ? detail : 'Failed to add user');
      }
    } catch (err) {
      setError('Connection error');
    }

    setAdding(false);
  };

  const handleChangePassword = async () => {
    setPwError('');
    setPwSuccess('');
    setChangingPw(true);

    try {
      const res = await fetch(`${API}/admin/users/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: selectedUserEmail, password: changePassword })
      });

      const data = await res.json();

      if (res.ok) {
        setPwSuccess('Password updated successfully');
        setChangePassword('');
        setTimeout(() => { setShowPasswordDialog(false); setPwSuccess(''); }, 1500);
      } else {
        setPwError(typeof data.detail === 'string' ? data.detail : 'Failed to update password');
      }
    } catch (err) {
      setPwError('Connection error');
    }

    setChangingPw(false);
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Delete user ${email}?`)) return;

    try {
      const res = await fetch(`${API}/admin/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        fetchUsers();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-white">User Management</h1>
              <p className="text-xs text-zinc-500">Add or remove users</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/api-test')}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="api-test-btn"
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              Test
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/api-settings')}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="api-settings-btn"
            >
              <Server className="w-4 h-4 mr-2" />
              API
            </Button>
            <Button
              size="sm"
              onClick={() => setShowAddDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 text-zinc-500 animate-spin mx-auto" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">No users found</div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {users.map((user) => (
                <div key={user.email} className="flex items-center justify-between p-4" data-testid={`user-row-${user.email}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.role === 'admin' ? 'bg-amber-600' : 'bg-zinc-700'}`}>
                      {user.role === 'admin' ? <Shield className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-white" />}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{user.name}</p>
                      <p className="text-zinc-500 text-xs">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${user.role === 'admin' ? 'bg-amber-900/30 text-amber-400' : 'bg-zinc-800 text-zinc-400'}`}>
                      {user.role}
                    </span>
                    <button
                      onClick={() => { setSelectedUserEmail(user.email); setChangePassword(''); setPwError(''); setPwSuccess(''); setShowPasswordDialog(true); }}
                      className="p-2 text-zinc-500 hover:text-amber-400 hover:bg-amber-900/20 rounded"
                      title="Change password"
                      data-testid={`change-pw-btn-${user.email}`}
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    {user.role !== 'admin' && (
                      <button
                        onClick={() => handleDeleteUser(user.email)}
                        className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-900/20 rounded"
                        data-testid={`delete-user-btn-${user.email}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-400" />
              Add New User
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Create account with email and password
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Name</label>
              <Input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="John Doe" className="bg-zinc-800 border-zinc-700 text-white" data-testid="new-user-name" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Email</label>
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="john@company.com" className="bg-zinc-800 border-zinc-700 text-white" data-testid="new-user-email" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Password</label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Set password" className="bg-zinc-800 border-zinc-700 text-white" data-testid="new-user-password" />
            </div>

            {error && (
              <div className="p-3 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400 text-sm">{error}</div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setError(''); }} className="bg-transparent border-zinc-700 text-zinc-300">Cancel</Button>
            <Button onClick={handleAddUser} disabled={adding || !newEmail || !newName || !newPassword} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-add-user">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-400" />
              Change Password
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedUserEmail}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">New Password</label>
              <Input type="password" value={changePassword} onChange={(e) => setChangePassword(e.target.value)} placeholder="Enter new password" className="bg-zinc-800 border-zinc-700 text-white" data-testid="change-password-input" />
            </div>

            {pwError && <div className="p-3 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400 text-sm">{pwError}</div>}
            {pwSuccess && <div className="p-3 bg-emerald-900/20 border border-emerald-800/50 rounded-lg text-emerald-400 text-sm">{pwSuccess}</div>}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="bg-transparent border-zinc-700 text-zinc-300">Cancel</Button>
            <Button onClick={handleChangePassword} disabled={changingPw || !changePassword} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-change-pw">
              {changingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
